import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useImpersonationService } from '../../services/impersonationService';
import ImpersonateModal from './ImpersonateModal';

const STATUSES = ['Active', 'Pending', 'Deleted'];
const LANGUAGES = ['en', 'uk', 'it', 'es', 'fr', 'de', 'cs', 'pl', 'bg'];
const TEAM_ROLES = ['Owner', 'Manager', 'Viewer'];

// table column keys the API can sort by
const SORTABLE = ['name', 'email', 'phone', 'systemRole', 'balance', 'language', 'status', 'createdAt'];

const fullName = row => [row.firstName, row.lastName].filter(Boolean).join(' ');

/** One realm of the Users page (#790): server-side search, filters, sort and paging, with the Impersonate action. */
export default function ImpersonationUsersTable({ realm }) {
    const { t } = useTranslation('impersonation');
    const { searchUsers } = useImpersonationService();
    const [query, setQuery] = useState({ search: '', filters: {}, sort: null, page: 1, pageSize: 20 });
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [target, setTarget] = useState(null);
    const searchTimer = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setData(
                await searchUsers({
                    realm,
                    page: query.page,
                    pageSize: query.pageSize,
                    ...(query.search ? { search: query.search } : {}),
                    ...(Object.keys(query.filters).length ? { filters: query.filters } : {}),
                    ...(query.sort ? { sort: query.sort } : {}),
                })
            );
        } catch (error) {
            setData({ items: [], total: 0 });
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realm, query]);

    useEffect(() => {
        load();
    }, [load]);

    const onSearch = value => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(
            () => setQuery(current => ({ ...current, search: value.trim(), page: 1 })),
            400
        );
    };

    const onTableChange = (pagination, tableFilters, sorter) => {
        const filters = {};
        if (tableFilters.status?.length) filters.status = tableFilters.status;
        if (tableFilters.language?.length) filters.language = tableFilters.language;
        if (tableFilters.twoFactorEnabled?.length === 1) filters.twoFactorEnabled = tableFilters.twoFactorEnabled[0];
        if (tableFilters.teams?.length) filters.teamRole = tableFilters.teams;

        const sort =
            sorter?.order && SORTABLE.includes(sorter.columnKey)
                ? { field: sorter.columnKey, order: sorter.order === 'ascend' ? 'ASC' : 'DESC' }
                : null;

        setQuery(current => ({
            ...current,
            filters,
            sort,
            page: pagination.current,
            pageSize: pagination.pageSize,
        }));
    };

    const columns = useMemo(
        () =>
            [
                {
                    key: 'name',
                    title: t('columns.name'),
                    sorter: true,
                    render: (_, row) => fullName(row) || '—',
                },
                { key: 'email', dataIndex: 'email', title: t('columns.email'), sorter: true },
                {
                    key: 'phone',
                    dataIndex: 'phoneNumber',
                    title: t('columns.phone'),
                    sorter: true,
                    render: value => value || '—',
                },
                { key: 'systemRole', dataIndex: 'systemRole', title: t('columns.role'), sorter: true },
                realm === 'dealer'
                    ? {
                          key: 'teams',
                          title: t('columns.teams'),
                          filters: TEAM_ROLES.map(role => ({ text: role, value: role })),
                          render: (_, row) =>
                              row.teams?.length ? (
                                  <Space direction="vertical" size={0}>
                                      {row.teams.map(team => (
                                          <Typography.Text key={team.teamId}>
                                              {team.teamName} · {team.role}
                                              {team.tier ? ` · ${team.tier}` : ''}
                                              {team.subscriptionStatus && team.subscriptionStatus !== 'Active'
                                                  ? ` (${team.subscriptionStatus})`
                                                  : ''}
                                          </Typography.Text>
                                      ))}
                                  </Space>
                              ) : (
                                  '—'
                              ),
                      }
                    : null,
                {
                    key: 'balance',
                    dataIndex: 'balance',
                    title: realm === 'dealer' ? t('columns.tier') : t('columns.credits'),
                    sorter: true,
                },
                {
                    key: 'language',
                    dataIndex: 'language',
                    title: t('columns.language'),
                    sorter: true,
                    filters: LANGUAGES.map(language => ({ text: language.toUpperCase(), value: language })),
                    render: value => value?.toUpperCase(),
                },
                {
                    key: 'status',
                    dataIndex: 'status',
                    title: t('columns.status'),
                    sorter: true,
                    filters: STATUSES.map(status => ({ text: status, value: status })),
                    render: value => (
                        <Tag color={value === 'Active' ? 'green' : value === 'Pending' ? 'gold' : 'default'}>
                            {value}
                        </Tag>
                    ),
                },
                {
                    key: 'twoFactorEnabled',
                    dataIndex: 'twoFactorEnabled',
                    title: t('columns.twoFactor'),
                    filters: [
                        { text: t('yes'), value: true },
                        { text: t('no'), value: false },
                    ],
                    render: value => (value ? t('yes') : t('no')),
                },
                {
                    key: 'createdAt',
                    dataIndex: 'createdAt',
                    title: t('columns.createdAt'),
                    sorter: true,
                    render: value => dayjs(value).format('YYYY-MM-DD'),
                },
                {
                    key: 'action',
                    title: '',
                    fixed: 'right',
                    render: (_, row) =>
                        row.impersonatable ? (
                            <Button
                                size="small"
                                onClick={() => setTarget({ realm, id: row.id, name: fullName(row), email: row.email })}
                            >
                                {t('impersonate')}
                            </Button>
                        ) : (
                            <Tooltip title={t('notImpersonatable')}>
                                <Button size="small" disabled>
                                    {t('impersonate')}
                                </Button>
                            </Tooltip>
                        ),
                },
            ].filter(Boolean),
        [realm, t]
    );

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Input.Search
                allowClear
                maxLength={100}
                placeholder={t('searchPlaceholder')}
                onChange={event => onSearch(event.target.value)}
                onSearch={onSearch}
                style={{ maxWidth: 420 }}
            />
            <Table
                rowKey="id"
                size="middle"
                loading={loading}
                columns={columns}
                dataSource={data.items}
                onChange={onTableChange}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: query.page,
                    pageSize: query.pageSize,
                    total: data.total,
                    showSizeChanger: true,
                    pageSizeOptions: [20, 50, 100],
                }}
            />
            {target && <ImpersonateModal target={target} onClose={() => setTarget(null)} />}
        </Space>
    );
}
